import { type FunctionComponent, useState } from 'react';

import { Tooltip } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { useAppMessaging } from '../../../hooks/useAppMessaging';
import BreakoutRoomOptionsContainer from '../BreakoutRoomOptionsContainer';

interface RoomsPopupListProps {
    userId: string;
    showMainSession: boolean;
}

const RoomsPopupList: FunctionComponent<RoomsPopupListProps> = ({ userId, showMainSession }) => {
    const { t } = useTranslation('breakout_room');
    const { sendMessage } = useAppMessaging();
    const { roomState } = useContainer(BreakoutRoomOptionsContainer);
    const [disable, setDisable] = useState(false);

    const handleRoomSelected = (roomId: string) => () => {
        // invite back to main classroom requires user to accept/decline,
        // disable this option once used
        if (roomId === '0') {
            setDisable(true);
        }

        sendMessage({
            payload: { func: 'assign_breakout_user', user_id: userId, room_id: roomId }
        });
    };

    const mainSessionTooltip = disable ? 'Invite sent' : '';

    return (
        <List sx={{ width: '100%', maxWidth: 350, bgcolor: 'background.paper' }}>
            {showMainSession && <Tooltip title={mainSessionTooltip}>
                <ListItem key="0" disablePadding disabled={disable}>
                    <ListItemButton role={undefined} onClick={handleRoomSelected('0')} dense>
                        <ListItemIcon>
                            <Checkbox
                                edge="end"
                                tabIndex={-1}
                                disableRipple
                                inputProps={{ 'aria-labelledby': 'MainSession' }}
                            />
                        </ListItemIcon>
                        <ListItemText id={'0'} primary={t('main-session')} />
                    </ListItemButton>
                </ListItem>
            </Tooltip>}

            {roomState.map((room) => {
                if (room.users.findIndex((user) => user.breakoutUserId === userId) >= 0) {
                    return null;
                }

                return (
                    <ListItem key={room.id} disablePadding >
                        <ListItemButton role={undefined} onClick={handleRoomSelected(room.id)} dense>
                            <ListItemIcon>
                                <Checkbox
                                    edge="end"
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-labelledby': room.name }}
                                />
                            </ListItemIcon>
                            <ListItemText id={room.name} primary={room.name} />
                        </ListItemButton>
                    </ListItem>
                );
            })}
        </List>
    );
};

export default RoomsPopupList;
